export const ANALYTICS_EVENTS = {
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
  PAGE_VIEW: 'page_view',

  AUTH_PROMPT_SHOWN: 'auth_prompt_shown',
  AUTH_PROMPT_DISMISSED: 'auth_prompt_dismissed',
  AUTH_LOGIN_SUCCESS: 'auth_login_success',
  AUTH_SIGNUP_SUCCESS: 'auth_signup_success',

  USER_ONBOARDING_STARTED: 'user_onboarding_started',
  USER_ONBOARDING_STEP_VIEWED: 'user_onboarding_step_viewed',
  USER_ONBOARDING_EXITED: 'user_onboarding_exited',

  FEED_VIEWED: 'feed_viewed',
  FEED_FILTER_CHANGED: 'feed_filter_changed',
  FEED_SORT_CHANGED: 'feed_sort_changed',
  FEED_SEARCH: 'feed_search',
  FEED_SEARCH_EMPTY: 'feed_search_empty',

  ITEM_CLICKED: 'item_clicked',
  ITEM_VIEWED: 'item_viewed',
  ITEM_SAVED: 'item_saved',
  ITEM_SHARED: 'item_shared',
  ITEM_SIMILAR_CLICKED: 'item_similar_clicked',

  ARTIST_LIST_VIEWED: 'artist_list_viewed',
  ARTIST_FILTER_CHANGED: 'artist_filter_changed',
  ARTIST_SEARCH: 'artist_search',
  ARTIST_CARD_CLICKED: 'artist_card_clicked',
  ARTIST_FOLLOW_CLICKED: 'artist_follow_clicked',
  ARTIST_TAB_CLICKED: 'artist_tab_clicked',
  ARTIST_VIEWED: 'artist_viewed',
  ARTIST_MESSAGE_CLICKED: 'artist_message_clicked',
  ARTIST_ITEM_CLICKED: 'artist_item_clicked',

  GUEST_TAB_CLICKED: 'guest_tab_clicked',
  GUEST_INSTAGRAM_CLICKED: 'guest_instagram_clicked',
  GUEST_REQUEST_ARTIST_CLICKED: 'guest_request_artist_clicked',
  GUEST_CITY_SELECTED: 'guest_city_selected',

  COLLECTION_CREATED: 'collection_created',
  COLLECTION_CLICKED: 'collection_clicked',
  COLLECTION_FIND_SIMILAR_CLICKED: 'collection_find_similar_clicked',
  COLLECTION_SIMILAR_ADDED: 'collection_similar_added',

  NAV_CLICK: 'nav_click',
  NAV_MORE_OPENED: 'nav_more_opened',

  CHAT_OPENED: 'chat_opened',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_IMAGE_UPLOADED: 'chat_image_uploaded',
  CHAT_MESSAGE_DELETED: 'chat_message_deleted',
  CHAT_AVAILABILITY_SENT: 'chat_availability_sent',
  CHAT_AVAILABILITY_READ: 'chat_availability_read',
  CHAT_AVAILABILITY_ACCEPTED: 'chat_availability_accepted',
  CHAT_OFF_PLATFORM_WARNING_ACKNOWLEDGED: 'chat_off_platform_warning_acknowledged',
  LEAD_CREATED: 'lead_created',

  QR_CODE_SCANNED: 'qr_code_scanned',

  TIME4TATS_TICKET_CLICKED: 'time4tats_ticket_clicked',

  MATCHER_SESSION_STARTED: 'matcher_session_started',
  MATCHER_CHOICE_MADE: 'matcher_choice_made',
  MATCHER_CHOICE_SKIPPED: 'matcher_choice_skipped',
  MATCHER_PAIRWISE_COMPLETED: 'matcher_pairwise_completed',
  MATCHER_PREFERENCES_SUBMITTED: 'matcher_preferences_submitted',
  MATCHER_RESULTS_VIEWED: 'matcher_results_viewed',
  MATCHER_RESULT_ARTIST_CLICKED: 'matcher_result_artist_clicked',
  MATCHER_SESSION_RESUMED: 'matcher_session_resumed',
  MATCHER_SESSION_EXITED: 'matcher_session_exited',
} as const;

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsDestination = 'ga' | 'posthog' | 'meta';

export interface AnalyticsEventDefinition {
  destinations: AnalyticsDestination[];
}

const defaultDestinations: AnalyticsDestination[] = ['ga', 'posthog'];
const metaDestinations: AnalyticsDestination[] = ['ga', 'posthog', 'meta'];

export const ANALYTICS_EVENT_DEFINITIONS: Record<AnalyticsEventName, AnalyticsEventDefinition> = {
  session_start: { destinations: defaultDestinations },
  session_end: { destinations: defaultDestinations },
  page_view: { destinations: defaultDestinations },

  auth_prompt_shown: { destinations: metaDestinations },
  auth_prompt_dismissed: { destinations: metaDestinations },
  auth_login_success: { destinations: metaDestinations },
  auth_signup_success: { destinations: metaDestinations },

  user_onboarding_started: { destinations: metaDestinations },
  user_onboarding_step_viewed: { destinations: metaDestinations },
  user_onboarding_exited: { destinations: metaDestinations },

  feed_viewed: { destinations: metaDestinations },
  feed_filter_changed: { destinations: metaDestinations },
  feed_sort_changed: { destinations: metaDestinations },
  feed_search: { destinations: metaDestinations },
  feed_search_empty: { destinations: metaDestinations },

  item_clicked: { destinations: metaDestinations },
  item_viewed: { destinations: metaDestinations },
  item_saved: { destinations: metaDestinations },
  item_shared: { destinations: metaDestinations },
  item_similar_clicked: { destinations: metaDestinations },

  artist_list_viewed: { destinations: metaDestinations },
  artist_filter_changed: { destinations: metaDestinations },
  artist_search: { destinations: metaDestinations },
  artist_card_clicked: { destinations: metaDestinations },
  artist_follow_clicked: { destinations: metaDestinations },
  artist_tab_clicked: { destinations: metaDestinations },
  artist_viewed: { destinations: metaDestinations },
  artist_message_clicked: { destinations: metaDestinations },
  artist_item_clicked: { destinations: metaDestinations },

  guest_tab_clicked: { destinations: metaDestinations },
  guest_instagram_clicked: { destinations: metaDestinations },
  guest_request_artist_clicked: { destinations: metaDestinations },
  guest_city_selected: { destinations: metaDestinations },

  collection_created: { destinations: metaDestinations },
  collection_clicked: { destinations: metaDestinations },
  collection_find_similar_clicked: { destinations: metaDestinations },
  collection_similar_added: { destinations: metaDestinations },

  nav_click: { destinations: metaDestinations },
  nav_more_opened: { destinations: metaDestinations },

  chat_opened: { destinations: metaDestinations },
  chat_message_sent: { destinations: metaDestinations },
  chat_image_uploaded: { destinations: metaDestinations },
  chat_message_deleted: { destinations: metaDestinations },
  chat_availability_sent: { destinations: metaDestinations },
  chat_availability_read: { destinations: metaDestinations },
  chat_availability_accepted: { destinations: metaDestinations },
  chat_off_platform_warning_acknowledged: { destinations: metaDestinations },
  lead_created: { destinations: metaDestinations },

  qr_code_scanned: { destinations: metaDestinations },

  time4tats_ticket_clicked: { destinations: metaDestinations },

  matcher_session_started: { destinations: metaDestinations },
  matcher_choice_made: { destinations: metaDestinations },
  matcher_choice_skipped: { destinations: metaDestinations },
  matcher_pairwise_completed: { destinations: metaDestinations },
  matcher_preferences_submitted: { destinations: metaDestinations },
  matcher_results_viewed: { destinations: metaDestinations },
  matcher_result_artist_clicked: { destinations: metaDestinations },
  matcher_session_resumed: { destinations: metaDestinations },
  matcher_session_exited: { destinations: metaDestinations },
};

export type AnalyticsEventPayloads = {
  session_start: { session_start_time: number };
  session_end: { session_duration: number };
  page_view: { page_path: string; page_title?: string; page_location?: string };

  auth_prompt_shown: { source: string; reason?: string };
  auth_prompt_dismissed: { source: string; reason?: string; dismiss_type?: string };
  auth_login_success: { method: string; source?: string };
  auth_signup_success: { method: string; source?: string; has_instagram?: boolean };

  user_onboarding_started: { total_steps: number };
  user_onboarding_step_viewed: { step_index: number; step_title: string; navigation_direction?: 'forward' | 'backward' | 'direct' };
  user_onboarding_exited: { exit_type: 'signup' | 'login'; completed_step_index: number; completed_step_title: string };

  feed_viewed: { feed_type: string };
  feed_filter_changed: { feed_type?: string; filter_type: string; filter_value: string };
  feed_sort_changed: { feed_type?: string; sort_key: string };
  feed_search: { feed_type?: string; query?: string; has_image: boolean; city?: string };
  feed_search_empty: {
    feed_type?: string;
    query?: string;
    has_image: boolean;
    city?: string;
    empty_reason: 'no_results' | 'search_failed';
    failure_message?: string;
  };

  item_clicked: { item_id: string; item_type: string; artist_id?: string; source?: string; feed_type?: string; position?: number };
  item_viewed: { item_id: string; item_type: string; artist_id?: string; source?: string };
  item_saved: { item_id: string; item_type: string; artist_id?: string; source?: string; is_saved: boolean };
  item_shared: { item_id: string; item_type: string; artist_id?: string; source?: string; share_method?: string; share_target?: string };
  item_similar_clicked: { item_id: string; item_type: string; artist_id?: string; source_item_id?: string; source_item_type?: string };

  artist_list_viewed: { tab?: string };
  artist_filter_changed: { filter_type: string; filter_value: string };
  artist_search: { query: string; city?: string };
  artist_card_clicked: { artist_id: string; position?: number; source?: string };
  artist_follow_clicked: { artist_id: string; is_following: boolean; source?: string };
  artist_tab_clicked: { tab_key: string; page?: string; artist_id?: string };
  artist_viewed: { artist_id: string; source?: string };
  artist_message_clicked: { artist_id: string; source?: string; item_id?: string; item_type?: string };
  artist_item_clicked: { artist_id: string; item_id: string; item_type: string; source?: string };

  guest_tab_clicked: { tab_key: string; city?: string };
  guest_instagram_clicked: { instagram_handle: string; city?: string };
  guest_request_artist_clicked: { city?: string; source?: string };
  guest_city_selected: { city: string };

  collection_created: { collection_id: string; title?: string; is_first?: boolean };
  collection_clicked: { collection_id: string };
  collection_find_similar_clicked: { collection_id: string };
  collection_similar_added: { collection_id: string; item_id: string; item_type: string };

  nav_click: { nav_type: 'bottom' | 'top' | 'more'; item_key: string; device?: string; platform?: string };
  nav_more_opened: { device?: string; platform?: string };

  chat_opened: { chat_id: string; role: 'artist' | 'user'; source?: string; artist_id?: string; user_id?: string };
  chat_message_sent: { chat_id: string; role: 'artist' | 'user'; message_type: 'text' | 'image' | 'mixed'; length?: number; image_count?: number; is_first?: boolean; artist_id?: string };
  chat_image_uploaded: { chat_id: string; role: 'artist' | 'user'; count: number; artist_id?: string };
  chat_message_deleted: { chat_id: string; role: 'artist' | 'user'; message_id?: string };
  chat_availability_sent: { chat_id: string; artist_id: string; mode: 'booking_system' | 'simple'; price_min?: number; price_max?: number; estimated_time?: number | 'unknown'; preparation_days?: number };
  chat_availability_read: { chat_id: string; artist_id: string; mode: 'booking_system' | 'simple'; message_id: string };
  chat_availability_accepted: { chat_id: string; artist_id: string; mode: 'booking_system' | 'simple'; booking_date?: string; booking_time?: string; booking_timestamp?: number; price?: number; price_min?: number; price_max?: number; estimated_time?: number | 'unknown' };
  chat_off_platform_warning_acknowledged: { chat_id?: string; artist_id?: string; artist_display_name?: string; matched_keyword: string };
  lead_created: {
    chat_id: string;
    artist_id?: string;
    lead_type?: 'flash' | 'custom';
    flash_id?: string | null;
    lead_source?: 'assistant_decision' | 'request_artist_button' | 'auto_on_leave';
  };

  qr_code_scanned: { qr_code: string; campaign?: string; redirect_destination?: string; timestamp?: number };

  time4tats_ticket_clicked: { event_id: string };

  matcher_session_started: { session_id: string; is_authenticated: boolean };
  matcher_choice_made: { session_id: string; step_index: number; chosen_option_id: string; total_choices: number; total_skips: number };
  matcher_choice_skipped: { session_id: string; step_index: number; total_skips: number };
  matcher_pairwise_completed: { session_id: string; total_choices: number; total_skips: number; skip_rate: number };
  matcher_preferences_submitted: { session_id: string; city: string; intent_mode: string; has_idea_text: boolean };
  matcher_results_viewed: { session_id: string; result_count: number; city: string; city_fallback_applied: boolean; intent_mode: string };
  matcher_result_artist_clicked: { session_id: string; artist_id: string; rank: number; intent_mode: string; destination: 'chat' | 'profile' };
  matcher_session_resumed: { session_id: string; session_status: string };
  matcher_session_exited: { session_id: string; step_index: number; flow_step: string };
};

export type AnalyticsEventPayloadsWithSession = {
  [K in keyof AnalyticsEventPayloads]: AnalyticsEventPayloads[K] & {
    session_id?: string;
    user_id?: string;
  };
};
